import { useState, useCallback } from 'react';

export interface UseAsyncState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseAsyncReturn<T, Args extends unknown[]> extends UseAsyncState<T> {
  execute: (...args: Args) => Promise<T | null>;
  reset: () => void;
  setData: (data: T | null) => void;
}

/**
 * Generic hook for handling async operations with loading/error states.
 * Does NOT auto-execute - call execute() manually.
 *
 * @example
 * const { execute, isLoading, error, data } = useAsync(
 *   async (id: string) => api.get(id)
 * );
 *
 * // Later...
 * await execute('123');
 */
export function useAsync<T, Args extends unknown[] = []>(
  asyncFn: (...args: Args) => Promise<T>,
  options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
    defaultErrorMessage?: string;
  }
): UseAsyncReturn<T, Args> {
  const [state, setState] = useState<UseAsyncState<T>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: Args): Promise<T | null> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const result = await asyncFn(...args);
        setState({ data: result, isLoading: false, error: null });
        options?.onSuccess?.(result);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : options?.defaultErrorMessage ?? 'An error occurred';
        setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
        options?.onError?.(err instanceof Error ? err : new Error(errorMessage));
        return null;
      }
    },
    [asyncFn, options]
  );

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  return {
    ...state,
    execute,
    reset,
    setData,
  };
}
